import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FiPlus, FiTrash2, FiEdit2, FiZap, FiCopy, FiUsers, FiArrowLeft, FiEye } from "react-icons/fi";
import { useCycle } from "../../context/CycleContext";
import {
  getAssignableEmployees, getKraList, getKpiList, getEmployeeKraKpi, getAssignmentPermissions,
  assignKra, updateKraAssignment, removeKraAssignment,
  assignKpi, updateKpiAssignment, removeKpiAssignment, assignToMany, balanceKpis, equalShares,
} from "../../services/kraKpiApi";
import { suggestForEmployee, applyTemplate } from "../../services/roleTemplateApi";
import { getKpisForKra } from "../../services/kraKpiProposalApi";
import useAsync from "../../hooks/useAsync";
import {
  Card, PageHeader, Button, DataTable, SearchInput, EmptyState, Banner, Modal, FormField, Loader, ConfirmDialog,
  DepartmentFilter, countByDepartment, inDepartment, OrgFilters, PersonCell, StatusBadge,
} from "../../components/common";
import { getLookups } from "../../services/employeeApi";
import useOrgFilters from "../../hooks/useOrgFilters"; // [NEW] shared organisation filters
import { singleValue } from "../../utils/orgFilters";
import Avatar from "../../components/common/Avatar";
import { directionLabel } from "../../components/performance/KraKpiTable";
import { workflowErrorText as getErrorMessage } from "../../utils/errors";
import Select from "../../components/common/Select"; // [NEW] alphabetical dropdown (replaces <select>)
import KraGoalExplainer from "../../components/goals/KraGoalExplainer";
import HrGoalReviewCard from "./components/HrGoalReviewCard"; // [NEW] PM-15: goals next to KRAs and KPIs
import { sortAlpha } from "../../utils/alphaSort"; // [NEW] A to Z

const api = {
  getAssignmentPermissions, getAssignableEmployees, getKraList, getKpiList, getEmployeeKraKpi, assignKra, updateKraAssignment, removeKraAssignment,
  assignKpi, updateKpiAssignment, removeKpiAssignment, suggestForEmployee, applyTemplate, assignToMany, getLookups, balanceKpis,
};

const kraFitsDepartment = (departmentId) => (kra) => !kra.departmentId || !departmentId || String(kra.departmentId) === String(departmentId);
// Option label that shows who owns the definition: organization-wide or the manager's own team.
const masterLabel = (m) => `${m.name}${m.scope === "TEAM" ? (m.ownedByMe ? " · my team" : " · team") : ""}`;

function BulkAssignModal({ open, people = [], masters, departments = [], onClose, onDone, cycleId }) {
  const [form, setForm] = useState({ type: "KRA", masterId: "", weightage: "", target: "", ids: [], search: "", departmentId: "" });
  const [busy, setBusy] = useState(false);
  const org = useOrgFilters({ list: people }); // [NEW] Business Unit, Location, Cost Centre, Legal Entity (Department stays above: it also decides which KRAs are listed)
  if (!open) return null;
  const list = ((form.type === "KRA" ? masters?.kra : masters?.kpi) || []).filter(form.type === "KRA" ? kraFitsDepartment(form.departmentId) : () => true);
  const q = form.search.toLowerCase();
  const chosenKra = form.type === "KRA" ? list.find((m) => m.id === form.masterId) : null;
  const shown = sortAlpha(people
    .filter(inDepartment(form.departmentId))
    .filter((p) => org.matches(p))
    .filter((p) => !chosenKra?.departmentId || String(p.departmentId) === String(chosenKra.departmentId))
    .filter((p) => !q || p.name.toLowerCase().includes(q) || (p.departmentName || "").toLowerCase().includes(q) || (p.designationName || "").toLowerCase().includes(q)));
  const counts = countByDepartment(people);
  const toggle = (id) => setForm((f) => ({ ...f, ids: f.ids.includes(id) ? f.ids.filter((x) => x !== id) : [...f.ids, id] }));
  const run = async () => {
    setBusy(true);
    try {
      await api.assignToMany(form.type, cycleId, form.ids, form);
      toast.success(`${form.type} assigned to ${form.ids.length} ${form.ids.length === 1 ? "person" : "people"}.`);
      setForm({ type: form.type, masterId: "", weightage: "", target: "", ids: [], search: "", departmentId: form.departmentId });
      onDone();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open={open}
      size="lg"
      title="Assign to several people"
      subtitle="All or nothing: if any chosen person can't take it (plan already submitted, weight over 100%, already assigned), nobody gets it."
      onClose={busy ? undefined : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button icon={FiUsers} onClick={run} loading={busy} disabled={!form.ids.length || !form.masterId}>Assign to {form.ids.length || ""} {form.ids.length === 1 ? "person" : "people"}</Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="form-grid">
          <FormField label="Department" hint="Shows that department's people and KRAs only.">
            <DepartmentFilter
              departments={departments.filter((d) => counts[d.id])}
              counts={counts}
              value={form.departmentId}
              onChange={(v) => setForm((f) => ({ ...f, departmentId: v, masterId: "", ids: f.ids.filter((id) => !v || String(people.find((p) => p.id === id)?.departmentId) === String(v)) }))}
            />
          </FormField>
          <FormField label="Type">
            <Select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value, masterId: "" }))}>
              <option value="KRA">KRA</option>
              <option value="KPI">KPI</option>
            </Select>
          </FormField>
          <FormField label={`${form.type} from the library`} required>
            <Select value={form.masterId} onChange={(e) => {
              const id = e.target.value;
              const kra = form.type === "KRA" ? list.find((m) => m.id === id) : null;
              setForm((f) => ({ ...f, masterId: id, ids: kra?.departmentId ? f.ids.filter((pid) => String(people.find((p) => p.id === pid)?.departmentId) === String(kra.departmentId)) : f.ids }));
            }}>
              <option value="">Choose a {form.type}</option>
              {list.filter((m) => m.active).map((m) => <option key={m.id} value={m.id}>{masterLabel(m)}</option>)}
            </Select>
          </FormField>
          {form.type === "KRA" && <FormField label="Goal %" required><input type="number" min="1" max="100" value={form.weightage} onChange={(e) => setForm((f) => ({ ...f, weightage: e.target.value }))} /></FormField>}
          <FormField label="Target" required><input type="number" step="any" value={form.target} onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))} /></FormField>
        </div>
        <div className="filter-bar">
          <OrgFilters org={org} fields={["businessUnit", "location", "costCenter", "legalEntity"]} />
          <SearchInput value={form.search} onChange={(v) => setForm((f) => ({ ...f, search: v }))} placeholder="Filter by name, department or designation" />
          <Button size="sm" variant="ghost" onClick={() => setForm((f) => ({ ...f, ids: [...new Set([...f.ids, ...shown.map((p) => p.id)])] }))}>Select all shown</Button>
          <Button size="sm" variant="ghost" onClick={() => setForm((f) => ({ ...f, ids: [] }))}>Clear</Button>
        </div>
        <ul className="picker-list bulk-picker">
          {shown.map((p) => (
            <li key={p.id}>
              <label className="checkbox">
                <input type="checkbox" checked={form.ids.includes(p.id)} onChange={() => toggle(p.id)} />
                <span>{p.name} <small className="muted">{p.designationName}, {p.departmentName}</small></span>
              </label>
            </li>
          ))}
          {!shown.length && <li className="muted picker-empty">No employees found.</li>}
        </ul>
      </div>
    </Modal>
  );
}

function WeightChip({ value }) {
  const tone = value === 100 ? "success" : value > 100 ? "danger" : "warning";
  return <span className={`badge badge-${tone}`}><span className="badge-dot" />{value}% of 100%</span>;
}

const templateTotal = (lines) => lines.reduce((sum, line) => sum + Number(line.weightage || 0), 0);

function TemplateSuggestion({ template, hasExisting, locked, onApply, busy, team = false }) {
  const [preview, setPreview] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);

  if (locked) return null;

  if (!template) {
    return (
      <Banner tone="neutral" title="No role template for this designation">
        {team ? (
          <>KRAs and KPIs here have to be added by hand. Create and map a team template on the{" "}
            <Link to="/team/role-templates">Role/Performance templates</Link> page and the next plan for this designation starts pre-filled.</>
        ) : (
          <>KRAs and KPIs here have to be added by hand. Add a template on the{" "}
            <Link to="/hr/role-templates">Role templates</Link> page and the next plan for this designation starts pre-filled.</>
        )}
      </Banner>
    );
  }

  return (
    <>
      <Banner
        tone="info"
        title={`${template.scope === "TEAM" ? "Team template" : "Template"} available: ${template.designationName}`}
        action={
          <Button size="sm" icon={FiZap} onClick={() => setPreview(true)}>
            {hasExisting ? "Review template" : "Auto-fill plan"}
          </Button>
        }
      >
        {template.kras.length} KRAs and {template.kpis.length} KPIs with weights and default targets, ready to apply.
        {hasExisting && " This employee already has items assigned, so you can top up or replace them."}
      </Banner>

      <Modal
        open={preview}
        size="lg"
        title={`Apply the ${template.designationName} template`}
        subtitle="Everything below is editable after it is applied."
        onClose={busy ? undefined : () => setPreview(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPreview(false)} disabled={busy}>Cancel</Button>
            <Button
              icon={FiCopy}
              loading={busy}
              onClick={async () => {
                const ok = await onApply(template.id, replaceExisting);
                if (ok) setPreview(false);
              }}
            >
              {replaceExisting ? "Replace and apply" : "Apply template"}
            </Button>
          </>
        }
      >
        <div className="form-stack">
          {hasExisting && (
            <FormField label="This employee already has items assigned">
              <div className="segmented" role="radiogroup" aria-label="How to apply the template">
                <button type="button" role="radio" aria-checked={!replaceExisting} className={!replaceExisting ? "is-active" : ""} onClick={() => setReplaceExisting(false)}>
                  Add what is missing
                </button>
                <button type="button" role="radio" aria-checked={replaceExisting} className={replaceExisting ? "is-active" : ""} onClick={() => setReplaceExisting(true)}>
                  Replace everything
                </button>
              </div>
            </FormField>
          )}

          <div className="template-preview">
            <div className="template-preview-head">
              <h4 className="section-title">KRAs</h4>
              <WeightChip value={templateTotal(template.kras)} />
            </div>
            <DataTable
              dense
              rows={template.kras}
              rowKey="kraId"
              columns={[
                { key: "name", label: "KRA", render: (r) => <span className="cell-stack"><strong>{r.name}</strong><small>{r.measurementMethod}</small></span> },
                { key: "weightage", label: "Weight", align: "right", render: (r) => `${r.weightage}%` },
                { key: "target", label: "Default target", align: "right" },
              ]}
            />
            <div className="template-preview-head">
              <h4 className="section-title">KPIs</h4>
              <span className="muted">KPIs share their KRA equally</span>
            </div>
            <DataTable
              dense
              rows={template.kpis}
              rowKey="kpiId"
              emptyTitle="No KPIs in this template"
              columns={[
                { key: "name", label: "KPI", render: (r) => <span className="cell-stack"><strong>{r.name}</strong><small>{r.unit}, {r.frequency}</small></span> },
                { key: "parent", label: "Parent KRA", render: (r) => template.kras.find((kra) => String(kra.kraId) === String(r.parentKraId))?.name || "—" },
                { key: "target", label: "Default target", align: "right" },
              ]}
            />
          </div>
          <p className="footnote">
            Targets come from the template as a starting point. Adjust anything that does not fit this person before the
            cycle opens, and the employee's goal sheet picks up the same numbers.
          </p>
        </div>
      </Modal>
    </>
  );
}

function KraKpiSubList({ kraId, kraName, assignedKpiIds, picks, onChange, allowedIds = null }) {
  const kpis = useAsync(() => getKpisForKra(kraId), [kraId], { skip: !kraId });
  // Managers may use only organization-wide KPIs and their own team KPIs.
  const list = (kpis.data || []).filter((k) => k.active && (!allowedIds || allowedIds.has(k.id)));
  const picked = Object.keys(picks);

  // [UPDATED] No KPI weight is typed: only a target. The service shares the KRA equally between its KPIs.
  const toggle = (kpi) => {
    const next = { ...picks };
    if (next[kpi.id]) delete next[kpi.id];
    else next[kpi.id] = { weightage: "0", target: "" };
    onChange(next);
  };
  const set = (id, field, value) => onChange({ ...picks, [id]: { ...picks[id], [field]: value } });
  const all = () => {
    const free = list.filter((k) => !assignedKpiIds.includes(k.id) && !picks[k.id]);
    if (!free.length) return;
    const next = { ...picks };
    free.forEach((k) => { next[k.id] = { weightage: "0", target: "" }; });
    onChange(next);
  };

  return (
    <div className="kra-kpi-sub">
      <div className="kra-kpi-sub-head">
        <span><strong>KPIs of {kraName}</strong> <span className="muted">tick the ones to assign with this KRA</span></span>
        {list.some((k) => !assignedKpiIds.includes(k.id) && !picks[k.id]) && <button type="button" className="link-btn" onClick={all}>Select all</button>}
      </div>
      {kpis.loading && !kpis.data && <p className="muted kra-kpi-sub-empty">Loading KPIs...</p>}
      {kpis.error && <p className="muted kra-kpi-sub-empty">{kpis.error}</p>}
      {kpis.data && !list.length && (
        <p className="muted kra-kpi-sub-empty">No KPI is mapped to this KRA yet. Map KPIs to it in the KPI library (Parent KRA), or assign the KRA alone.</p>
      )}
      <ul className="kra-kpi-sub-list">
        {list.map((k) => {
          const already = assignedKpiIds.includes(k.id);
          const pick = picks[k.id];
          return (
            <li key={k.id} className={`${pick ? "is-picked" : ""} ${already ? "is-assigned" : ""}`}>
              <label className="checkbox">
                <input type="checkbox" checked={already || Boolean(pick)} disabled={already} onChange={() => toggle(k)} />
                <span className="cell-stack">
                  <strong>{k.name}</strong>
                  <small>{already ? "Already assigned" : `${k.unit}, ${k.frequency}, ${directionLabel(k.direction).toLowerCase()}`}</small>
                </span>
              </label>
              {pick && (
                <span className="kra-kpi-sub-inputs">
                  <input className="form-control" type="number" step="any" placeholder={`Target (${k.unit})`} value={pick.target} onChange={(e) => set(k.id, "target", e.target.value)} aria-label={`${k.name} target`} />
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {picked.length > 0 && (
        <p className="kra-kpi-sub-foot">
          {picked.length} KPI{picked.length === 1 ? "" : "s"} selected. No KPI weight to type: they share this KRA equally ({equalShares(picked.length + assignedKpiIds.length).join(" + ")}%), so the KRA score is the average of its KPIs. Enter a target for each.
        </p>
      )}
    </div>
  );
}

function AssignmentSection({ type, rows, total, master: fullMaster, locked, reviewOnly = false, onAdd, onEdit, onRemove, kraOptions = null, kpiRows = null, kpiMaster = [], onAddWithKpis = null, departmentId = null, departmentName = "" }) {
  const master = type === "KRA" ? fullMaster.filter(kraFitsDepartment(departmentId)) : fullMaster;
  const assignedIds = rows.map((r) => r[type === "KRA" ? "kraId" : "kpiId"]);
  const [kraFilter, setKraFilter] = useState({ kraId: "", parentKraItemId: "", kpiIds: type === "KPI" ? [] : null, loading: false });
  const available = master.filter((m) => m.active && !assignedIds.includes(m.id) && (!kraFilter.kpiIds || kraFilter.kpiIds.includes(m.id)));
  const [draft, setDraft] = useState({ refId: "", parentKraItemId: "", weightage: "", target: "" });
  const [busy, setBusy] = useState(false);
  const [kpiPicks, setKpiPicks] = useState({});
  const withKpis = type === "KRA" && Boolean(onAddWithKpis);
  const assignedKpiIds = (kpiRows || []).map((r) => r.kpiId);

  const filterByKra = async (parentKraItemId) => {
    const parent = (kraOptions || []).find((kra) => kra.id === parentKraItemId);
    setDraft((d) => ({ ...d, refId: "", parentKraItemId: parentKraItemId || "" }));
    if (!parent) { setKraFilter({ kraId: "", parentKraItemId: "", kpiIds: [], loading: false }); return; }
    setKraFilter({ kraId: parent.kraId, parentKraItemId, kpiIds: [], loading: true });
    try {
      const { data } = await getKpisForKra(parent.kraId);
      setKraFilter({ kraId: parent.kraId, parentKraItemId, kpiIds: data.map((k) => k.id), loading: false });
    } catch (err) {
      toast.error(getErrorMessage(err));
      setKraFilter({ kraId: "", parentKraItemId: "", kpiIds: [], loading: false });
    }
  };

  const pick = (id) => {
    setKpiPicks({});
    const m = master.find((x) => x.id === id);
    // [UPDATED] No KPI weight is typed (shared equally by the service).
    setDraft({ ...draft, refId: id, weightage: type === "KPI" ? "0" : m ? String(Math.max(0, 100 - total)) : "" });
  };

  const add = async () => {
    if (!draft.refId) { toast.error(`Choose a ${type} to assign.`); return; }
    const picks = Object.entries(kpiPicks);
    if (withKpis && picks.length) {
      const bad = picks.find(([, v]) => !(Number(v.target) > 0));
      if (bad) {
        const name = kpiMaster.find((m) => m.id === bad[0])?.name || "a KPI";
        toast.error(`Enter a target above 0 for ${name}.`);
        return;
      }
    }
    setBusy(true);
    const ok = withKpis && picks.length ? await onAddWithKpis(draft, picks.map(([kpiId, v]) => ({ kpiId, ...v }))) : await onAdd(draft);
    setBusy(false);
    if (ok) { setDraft({ refId: "", parentKraItemId: type === "KPI" ? kraFilter.parentKraItemId : "", weightage: "", target: "" }); setKpiPicks({}); }
  };
  const pickedCount = Object.keys(kpiPicks).length;
  const kraName = type === "KRA" ? master.find((m) => m.id === draft.refId)?.name : "";
  const kpisUnder = (kraItemId) => (kpiRows || []).filter((r) => r.parentKraItemId === kraItemId).map((r) => r.name);
  const parentName = (kpiId) => {
    const row = rows.find((item) => item.kpiId === kpiId);
    return row?.parentKraName || (kraOptions || []).find((kra) => kra.id === row?.parentKraItemId)?.name || null;
  };

  return (
    <Card
      title={`${type}s`}
      subtitle={type === "KRA" && departmentName && departmentName !== "—" ? `Showing KRAs for ${departmentName} and common KRAs (all departments).` : undefined}
      actions={type === "KRA" ? <WeightChip value={total} /> : <span className="muted">KPIs share their KRA equally: each KRA scores the average of its KPIs</span>}
      padded={false}
    >
      {/* [UPDATED] PM-16: in HR review mode there is no "Assign" bar; employees add their own KRAs and KPIs. */}
      {!locked && !reviewOnly && (
        <>
        {kraOptions && (
          <div className="assign-bar assign-bar-filter">
            <Select className="form-control" value={kraFilter.parentKraItemId} onChange={(e) => filterByKra(e.target.value)} aria-label="Choose parent KRA for KPI">
              <option value="">Choose the assigned parent KRA</option>
              {kraOptions.map((k) => <option key={k.id} value={k.id}>KPIs of: {k.name}</option>)}
            </Select>
          </div>
        )}
        <div className="assign-bar">
          <Select className="form-control" value={draft.refId} onChange={(e) => pick(e.target.value)} aria-label={`Choose ${type}`}>
            <option value="">{kraFilter.loading ? "Loading..." : type === "KPI" && !kraFilter.parentKraItemId ? "Choose a parent KRA first" : kraFilter.kpiIds && !available.length ? "No unassigned KPI is mapped to this KRA" : `Choose a ${type} from the library`}</option>
            {available.map((m) => <option key={m.id} value={m.id}>{masterLabel(m)}</option>)}
          </Select>
          {type === "KRA" && <input className="form-control" type="number" min="1" max="100" placeholder="Goal %" value={draft.weightage} onChange={(e) => setDraft({ ...draft, weightage: e.target.value })} aria-label="Goal percentage" />}
          <input className="form-control" type="number" step="any" placeholder="Target" value={draft.target} onChange={(e) => setDraft({ ...draft, target: e.target.value })} aria-label="Target" />
          <Button icon={FiPlus} onClick={add} loading={busy}>{pickedCount ? `Assign KRA + ${pickedCount} KPI${pickedCount === 1 ? "" : "s"}` : "Assign"}</Button>
        </div>
        {withKpis && draft.refId && (
          <KraKpiSubList kraId={draft.refId} kraName={kraName} assignedKpiIds={assignedKpiIds} picks={kpiPicks} onChange={setKpiPicks} allowedIds={new Set(kpiMaster.map((m) => m.id))} />
        )}
        </>
      )}
      <DataTable
        dense
        rows={rows}
        emptyTitle={`No ${type}s assigned`}
        emptyText={reviewOnly ? "The employee has not added any yet." : "Pick one from the library above."}
        columns={[
          {
            key: "name", label: type,
            render: (r) => {
              const under = type === "KRA" ? kpisUnder(r.id) : [];
              const parent = type === "KPI" ? parentName(r.kpiId) : null;
              return (
                <span className="cell-stack">
                  <strong>{r.name}</strong>
                  <small>{type === "KRA" ? r.measurementMethod : `${r.unit}, ${r.frequency}, ${directionLabel(r.direction).toLowerCase()}`}</small>
                  {under.length > 0 && <small className="kra-kpi-under">KPIs: {under.join(", ")}</small>}
                  {parent && <small className="kra-kpi-under">Under KRA: {parent}</small>}
                </span>
              );
            },
          },
          ...(type === "KRA" ? [{ key: "weightage", label: "Goal %", align: "right", render: (r) => <span className="goal-pct-chip">{r.weightage}%</span> }] : []),
          { key: "target", label: "Target", align: "right" },
          { key: "actual", label: "Actual", align: "right", render: (r) => r.actual ?? "—" },
          {
            key: "act", label: "", align: "right",
            render: (r) => !locked && (
              <span className="row-actions">
                <Button variant="ghost" size="sm" icon={FiEdit2} aria-label={`Edit ${r.name}`} onClick={() => onEdit(r)} />
                {!reviewOnly && <Button variant="ghost" size="sm" icon={FiTrash2} aria-label={`Remove ${r.name}`} onClick={() => onRemove(r)} />}
              </span>
            ),
          },
        ]}
      />
    </Card>
  );
}

// [UPDATED] Requirement 5: KRAs and KPIs are created by the employee; managers never assign them. This page is the
// HR review/correction page only, so the old manager "team" scope is no longer accepted (always "org").
export default function KraKpiAssignmentPage() {
  const scope = "org";
  const { cycleId, cycle } = useCycle();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("employee");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const lookups = useAsync(() => api.getLookups(), []);
  const people = useAsync(() => api.getAssignableEmployees(cycleId, scope), [cycleId, scope], { skip: !cycleId });
  const orgFilter = useOrgFilters({ list: people.data ?? null }); // [NEW] Business Unit, Department, Location, Cost Centre, Legal Entity
  const team = false; // manager assignment removed (requirement 5)
  const perms = useAsync(() => api.getAssignmentPermissions(), []);
  const masters = useAsync(async () => {
    const [k, p] = await Promise.all([api.getKraList(), api.getKpiList()]);
    return { kra: k.data, kpi: p.data };
  }, []);
  const detail = useAsync(() => api.getEmployeeKraKpi(cycleId, selectedId), [cycleId, selectedId], { skip: !cycleId || !selectedId });
  const designationName = detail.data?.employee?.designationName;
  const designationId = detail.data?.employee?.designationId;
  const reviewerId = detail.data?.employee?.managerId;
  const template = useAsync(
    () => api.suggestForEmployee({ employeeId: selectedId, designationId, designationName, managerId: reviewerId }),
    [selectedId, designationId, designationName, reviewerId],
    { skip: !selectedId || !designationName }
  );

  const q = search.toLowerCase();
  const list = (people.data || [])
    .filter((p) => orgFilter.matches(p))
    .filter((p) => !q || p.name.toLowerCase().includes(q) || p.departmentName.toLowerCase().includes(q));
  const svc = (type) => (type === "KRA"
    ? { assign: api.assignKra, updateAssignment: api.updateKraAssignment, removeAssignment: api.removeKraAssignment }
    : { assign: api.assignKpi, updateAssignment: api.updateKpiAssignment, removeAssignment: api.removeKpiAssignment });

  const add = (type) => async (draft) => {
    try {
      await svc(type).assign(cycleId, selectedId, {
        [type === "KRA" ? "kraId" : "kpiId"]: draft.refId,
        parentKraItemId: draft.parentKraItemId,
        weightage: type === "KPI" ? undefined : draft.weightage, // KPI share is set by the service
        target: draft.target,
      });
      toast.success(`${type} assigned.`);
      detail.reload();
      return true;
    } catch (err) {
      toast.error(getErrorMessage(err));
      return false;
    }
  };

  const addKraWithKpis = async (draft, kpis) => {
    let parentKraItemId;
    try {
      const { data } = await api.assignKra(cycleId, selectedId, { kraId: draft.refId, weightage: draft.weightage, target: draft.target });
      parentKraItemId = data.item_ids?.[0];
      if (!parentKraItemId) throw new Error("The backend did not return the new KRA item ID.");
    } catch (err) {
      toast.error(getErrorMessage(err));
      return false;
    }
    const failed = [];
    for (const k of kpis) {
      try {
        await api.assignKpi(cycleId, selectedId, {
          kpiId: k.kpiId,
          parentKraItemId,
          target: k.target,
        });
      } catch (err) {
        const name = (masters.data?.kpi || []).find((m) => m.id === k.kpiId)?.name || "KPI";
        failed.push(`${name}: ${getErrorMessage(err)}`);
      }
    }
    detail.reload();
    if (failed.length) {
      toast.warning(`KRA assigned, but ${failed.length} KPI${failed.length === 1 ? "" : "s"} failed. ${failed.join(" ")}`);
    } else {
      toast.success(`KRA and ${kpis.length} KPI${kpis.length === 1 ? "" : "s"} assigned.`);
    }
    return true;
  };

  const applyRoleTemplate = async (templateId, replaceExisting) => {
    setBusy(true);
    try {
      await api.applyTemplate(cycleId, selectedId, { templateId, replaceExisting });
      toast.success(replaceExisting ? "Plan replaced from the role template." : "Plan filled from the role template.");
      detail.reload();
      return true;
    } catch (err) {
      toast.error(getErrorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    const reason = String(editing.reason || "").trim();
    if (!team && !reason) { toast.error("Please give a reason for the correction."); return; }
    setBusy(true);
    try {
      await svc(editing.type).updateAssignment(editing.row.id, {
        weightage: editing.type === "KPI" ? 0 : editing.weightage,
        target: editing.target,
        ...(team ? {} : { reason: `HR correction: ${reason}` }),
      });
      toast.success(team ? "Assignment updated." : "Correction saved. The employee and the manager can see it.");
      setEditing(null);
      detail.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const shareEqually = async (kraItemId) => {
    setBusy(true);
    try {
      await api.balanceKpis(detail.data.recordId, kraItemId, 0);
      toast.success("KPI weights shared equally.");
      detail.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await svc(removing.type).removeAssignment(removing.row.id);
      toast.success(`${removing.row.name} removed.`);
      setRemoving(null);
      detail.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const d = detail.data;
  // [NEW] PM-15/PM-16: HR (scope "org") reviews and edits KRAs and KPIs but no longer assigns them.
  const reviewOnly = !team;

  return (
    <div className="page">
      <PageHeader
        title={reviewOnly ? "KRA, KPI & goal review" : "Assign KRA & KPI"}
        subtitle={reviewOnly ? `Review and correct the KRAs and KPIs of any employee in ${cycle?.name}.` : `Set KRAs (daily tasks) and KPIs for your direct reports in ${cycle?.name}.`}
        actions={!reviewOnly && perms.data?.canAssign && <Button variant="secondary" icon={FiUsers} onClick={() => setBulkOpen(true)}>Assign to several people</Button>}
      />
      {!reviewOnly && <KraGoalExplainer view="manager" compact />}
      {reviewOnly && (
        <Banner tone="info" title="Review and edit only">
          Employees add their own KRAs, KPIs and goals and their manager approves them. Here HR can check every plan and
          correct a target, goal % or goal with a reason.
        </Banner>
      )}
      {!reviewOnly && perms.data && !perms.data.canAssign && (
        <Banner tone="warning" title="View only">
          Your role does not have the kra_kpi.assign permission. You can review the plans here; ask HR to change them,
          or ask an administrator to grant the permission to your role.
        </Banner>
      )}
      {/* [UPDATED] HR: a list of employees (filters in one row, wrapping when needed); a name opens the full view. */}
      {reviewOnly && !selectedId && (
        <>
          <div className="filter-bar review-filter-bar">
            <SearchInput value={search} onChange={setSearch} placeholder="Find an employee" />
            <OrgFilters org={orgFilter} />
          </div>
          <Card padded={false}>
            <DataTable
              label="Employees"
              rows={list}
              loading={people.loading && !people.data}
              onRowClick={(r) => setParams({ employee: r.id })}
              emptyTitle="No employees found"
              emptyText="Change the filters, or add employees to this cycle under Performance cycles."
              columns={[
                { key: "name", label: "Employee", sortValue: (r) => r.name, render: (r) => <PersonCell name={r.name} meta={r.designationName} /> },
                { key: "departmentName", label: "Department" },
                { key: "managerName", label: "Reports to" },
                { key: "goalCount", label: "Goals", align: "right" },
                { key: "kraCount", label: "KRAs", align: "right" },
                { key: "kpiCount", label: "KPIs", align: "right" },
                { key: "sheetStatus", label: "Goal sheet", render: (r) => <StatusBadge status={r.sheetStatus} /> },
                { key: "open", label: "", align: "right", render: (r) => <Button size="sm" variant="ghost" icon={FiEye} onClick={(e) => { e.stopPropagation(); setParams({ employee: r.id }); }}>View</Button> },
              ]}
            />
          </Card>
        </>
      )}
      {reviewOnly && selectedId && (
        <div className="stack">
          <div><Button variant="ghost" icon={FiArrowLeft} onClick={() => setParams({})}>All employees</Button></div>
          {!selectedId && <Card><EmptyState title="Choose an employee" text="Their KRAs and KPIs for this cycle will show here." /></Card>}
          {selectedId && detail.error && <Banner tone="danger" title="Couldn't load assignments">{detail.error}</Banner>}
          {selectedId && !d && !detail.error && <Loader />}
          {d && (
            <>
              <Card>
                <div className="profile-mini">
                  <Avatar name={d.employee.name} size={44} />
                  <div>
                    <p className="profile-mini-name">{d.employee.name}</p>
                    <p className="muted">{d.employee.designationName}, {d.employee.departmentName}. Reports to {d.employee.managerName}.</p>
                  </div>
                </div>
              </Card>
              {d.locked && <Banner tone="neutral" title="Assignments are locked">{d.lockReason}</Banner>}
              {[...(d.kras.length && d.kraWeightage !== 100
                ? [{ key: "KRA", title: `KRA weights add up to ${d.kraWeightage}%, not 100%` }]
                : []),
              ...d.kras.map((kra) => ({
                key: `KPI-${kra.id}`,
                title: (d.kpis || []).some((k) => k.parentKraItemId === kra.id)
                  ? `KPI weights under ${kra.name} add up to ${d.kpiWeightageByKraItem?.[kra.id] || 0}%, not 100%.`
                  : `${kra.name} has no KPI yet. Add at least one KPI under it.`,
                total: d.kpiWeightageByKraItem?.[kra.id] || 0,
                hasKpis: (d.kpis || []).some((k) => k.parentKraItemId === kra.id),
                kraItemId: kra.id,
              })).filter((row) => !row.hasKpis || row.total !== 100)]
                .map(({ key, title, kraItemId, hasKpis }) => (
                  <Banner key={key} tone={d.locked ? "danger" : "warning"} title={title}>
                    {d.locked
                      ? `This review can't get a final score. The backend no longer allows changes at this stage; ask the backend team to unlock it, or exclude and re-add the employee.`
                      : "Fix this before the employee submits goals. KRA weights must total 100% and every KRA needs at least one KPI (its KPIs share it equally)."}
                    {!d.locked && hasKpis && kraItemId && (
                      <div><Button size="sm" variant="secondary" loading={busy} onClick={() => shareEqually(kraItemId)}>Share KPI weights equally</Button></div>
                    )}
                  </Banner>
                ))}
              {!reviewOnly && !template.loading && (
                <TemplateSuggestion
                  template={template.data}
                  hasExisting={d.kras.length > 0 || d.kpis.length > 0}
                  locked={d.locked}
                  busy={busy}
                  team={team}
                  onApply={applyRoleTemplate}
                />
              )}
              <AssignmentSection
                type="KRA" rows={d.kras} total={d.kraWeightage} master={masters.data?.kra || []} locked={d.locked} reviewOnly={reviewOnly}
                departmentId={d.employee.departmentId} departmentName={d.employee.departmentName}
                kpiRows={d.kpis} kpiMaster={masters.data?.kpi || []} onAddWithKpis={addKraWithKpis}
                onAdd={add("KRA")}
                onEdit={(row) => setEditing({ type: "KRA", row, weightage: row.weightage, target: row.target, reason: "" })}
                onRemove={(row) => setRemoving({ type: "KRA", row })}
              />
              <AssignmentSection
                type="KPI" rows={d.kpis} total={d.kpiWeightage} master={masters.data?.kpi || []} locked={d.locked} reviewOnly={reviewOnly} kraOptions={d.kras} kpiRows={d.kpis}
                onAdd={add("KPI")}
                onEdit={(row) => setEditing({ type: "KPI", row, weightage: row.weightage, target: row.target, reason: "" })}
                onRemove={(row) => setRemoving({ type: "KPI", row })}
              />
              {reviewOnly && cycle && <HrGoalReviewCard cycle={cycle} employeeId={selectedId} employeeName={d.employee.name} />}
              <p className="footnote">KRAs are the daily tasks of the associate; KPIs measure them. KRA goal percentages must total exactly 100%. You do not type KPI weights: the KPIs of a KRA share it equally, so each KRA's score is the average of its KPIs. Only KRAs of the employee&apos;s department and common KRAs are offered.</p>
            </>
          )}
        </div>
      )}
      {!reviewOnly && (
      <div className="split-layout">
        <Card className="picker-card" padded={false}>
          <div className="picker-search"><SearchInput value={search} onChange={setSearch} placeholder="Find an employee" /></div>
          <div className="picker-filter">
            <OrgFilters org={orgFilter} />
          </div>
          {people.loading && !people.data ? <Loader /> : (
            <ul className="picker-list">
              {list.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className={`picker-item ${p.id === selectedId ? "is-active" : ""} ${p.inCycle === false ? "is-disabled" : ""}`}
                    disabled={p.inCycle === false}
                    title={p.inCycle === false ? "Not part of this cycle yet. HR adds employees under Performance cycles." : undefined}
                    onClick={() => setParams({ employee: p.id })}
                  >
                    <Avatar name={p.name} size={32} />
                    <span className="person-text">
                      <span className="person-name">{p.name}</span>
                      <span className="person-meta">{p.inCycle === false ? "Not in this cycle" : `${p.designationName}${singleValue(orgFilter.filters.departmentId) ? "" : `, ${p.departmentName}`}`}</span>
                    </span>
                  </button>
                </li>
              ))}
              {!list.length && <li className="muted picker-empty">No employees found.</li>}
            </ul>
          )}
        </Card>

        <div className="stack">
          {!selectedId && <Card><EmptyState title="Choose an employee" text="Their KRAs and KPIs for this cycle will show here." /></Card>}
          {selectedId && detail.error && <Banner tone="danger" title="Couldn't load assignments">{detail.error}</Banner>}
          {selectedId && !d && !detail.error && <Loader />}
          {d && (
            <>
              <Card>
                <div className="profile-mini">
                  <Avatar name={d.employee.name} size={44} />
                  <div>
                    <p className="profile-mini-name">{d.employee.name}</p>
                    <p className="muted">{d.employee.designationName}, {d.employee.departmentName}. Reports to {d.employee.managerName}.</p>
                  </div>
                </div>
              </Card>
              {d.locked && <Banner tone="neutral" title="Assignments are locked">{d.lockReason}</Banner>}
              {[...(d.kras.length && d.kraWeightage !== 100
                ? [{ key: "KRA", title: `KRA weights add up to ${d.kraWeightage}%, not 100%` }]
                : []),
              ...d.kras.map((kra) => ({
                key: `KPI-${kra.id}`,
                title: (d.kpis || []).some((k) => k.parentKraItemId === kra.id)
                  ? `KPI weights under ${kra.name} add up to ${d.kpiWeightageByKraItem?.[kra.id] || 0}%, not 100%.`
                  : `${kra.name} has no KPI yet. Add at least one KPI under it.`,
                total: d.kpiWeightageByKraItem?.[kra.id] || 0,
                hasKpis: (d.kpis || []).some((k) => k.parentKraItemId === kra.id),
                kraItemId: kra.id,
              })).filter((row) => !row.hasKpis || row.total !== 100)]
                .map(({ key, title, kraItemId, hasKpis }) => (
                  <Banner key={key} tone={d.locked ? "danger" : "warning"} title={title}>
                    {d.locked
                      ? `This review can't get a final score. The backend no longer allows changes at this stage; ask the backend team to unlock it, or exclude and re-add the employee.`
                      : "Fix this before the employee submits goals. KRA weights must total 100% and every KRA needs at least one KPI (its KPIs share it equally)."}
                    {!d.locked && hasKpis && kraItemId && (
                      <div><Button size="sm" variant="secondary" loading={busy} onClick={() => shareEqually(kraItemId)}>Share KPI weights equally</Button></div>
                    )}
                  </Banner>
                ))}
              {!reviewOnly && !template.loading && (
                <TemplateSuggestion
                  template={template.data}
                  hasExisting={d.kras.length > 0 || d.kpis.length > 0}
                  locked={d.locked}
                  busy={busy}
                  team={team}
                  onApply={applyRoleTemplate}
                />
              )}
              <AssignmentSection
                type="KRA" rows={d.kras} total={d.kraWeightage} master={masters.data?.kra || []} locked={d.locked} reviewOnly={reviewOnly}
                departmentId={d.employee.departmentId} departmentName={d.employee.departmentName}
                kpiRows={d.kpis} kpiMaster={masters.data?.kpi || []} onAddWithKpis={addKraWithKpis}
                onAdd={add("KRA")}
                onEdit={(row) => setEditing({ type: "KRA", row, weightage: row.weightage, target: row.target, reason: "" })}
                onRemove={(row) => setRemoving({ type: "KRA", row })}
              />
              <AssignmentSection
                type="KPI" rows={d.kpis} total={d.kpiWeightage} master={masters.data?.kpi || []} locked={d.locked} reviewOnly={reviewOnly} kraOptions={d.kras} kpiRows={d.kpis}
                onAdd={add("KPI")}
                onEdit={(row) => setEditing({ type: "KPI", row, weightage: row.weightage, target: row.target, reason: "" })}
                onRemove={(row) => setRemoving({ type: "KPI", row })}
              />
              {reviewOnly && cycle && <HrGoalReviewCard cycle={cycle} employeeId={selectedId} employeeName={d.employee.name} />}
              <p className="footnote">KRAs are the daily tasks of the associate; KPIs measure them. KRA goal percentages must total exactly 100%. You do not type KPI weights: the KPIs of a KRA share it equally, so each KRA's score is the average of its KPIs. Only KRAs of the employee&apos;s department and common KRAs are offered.</p>
            </>
          )}
        </div>
      </div>
      )}

      <Modal
        open={Boolean(editing)}
        size="sm"
        title={`Edit ${editing?.row.name}`}
        onClose={busy ? undefined : () => setEditing(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
            <Button onClick={saveEdit} loading={busy}>Save</Button>
          </>
        }
      >
        {editing && (
          <div className="form-stack">
            {editing.type !== "KPI" && <FormField label="Goal %"><input type="number" min="1" max="100" value={editing.weightage} onChange={(e) => setEditing({ ...editing, weightage: e.target.value })} /></FormField>}
            <FormField label="Target"><input type="number" step="any" value={editing.target} onChange={(e) => setEditing({ ...editing, target: e.target.value })} /></FormField>
            {reviewOnly && (
              <FormField label="Reason for HR correction" required hint="The employee and the manager can see it in the history.">
                <input value={editing.reason || ""} onChange={(e) => setEditing({ ...editing, reason: e.target.value })} placeholder="For example: target aligned with the KPI library" />
              </FormField>
            )}
          </div>
        )}
      </Modal>
      <BulkAssignModal
        open={bulkOpen}
        cycleId={cycleId}
        people={(people.data || []).filter((p) => p.inCycle !== false)}
        masters={masters.data}
        departments={lookups.data?.departments || []}
        onClose={() => setBulkOpen(false)}
        onDone={() => { setBulkOpen(false); detail.reload(); }}
      />
      <ConfirmDialog
        open={Boolean(removing)}
        title={`Remove ${removing?.row.name}?`}
        message="Any actual values entered for it will be lost."
        confirmLabel="Remove"
        tone="danger"
        loading={busy}
        onConfirm={remove}
        onClose={() => setRemoving(null)}
      />
    </div>
  );
}
